import { useState, useMemo } from "react"
import { Autocomplete, Button, Chip, Stack, TextField, Typography } from "@mui/material"
import { rateCards } from "routes/rate-cards/controller/rate-cards"
import { ruleSets } from "routes/rule-sets/controller/rule-sets"

export function AddVariantDialog({ ok, cancel, initialData }) {
    const [name, setName] = useState(initialData?.name || "")
    const [selectedRateCard, setSelectedRateCard] = useState(initialData?.rateCard || null)
    const [selectedRuleSets, setSelectedRuleSets] = useState(initialData?.ruleSets || [])

    const rateCardResult = rateCards.useResults({ skip: 0, take: 100, status: "active" })
    const rateCardItems = useMemo(getRateCardItems, [rateCardResult])

    const ruleSetResult = ruleSets.useResults({ skip: 0, take: 100, status: "active" })
    const ruleSetItems = ruleSetResult?.items ?? []

    const canSubmit = name.trim() && selectedRateCard

    return (
        <Stack spacing={3} sx={{ p: 3, minWidth: 400 }}>
            <Typography variant="h6">{dialogTitle()}</Typography>
            <TextField
                data-cy="variant-name"
                label="Variant Name"
                value={name}
                onChange={handleNameChange}
                placeholder="e.g. Budget Option, Premium..."
                fullWidth
                autoFocus
            />
            <Autocomplete
                data-cy="variant-rate-card"
                options={rateCardItems}
                getOptionLabel={getOptionLabel}
                value={selectedRateCard}
                onChange={handleRateCardChange}
                renderInput={renderRateCardInput}
                isOptionEqualToValue={isOptionEqual}
                fullWidth
            />
            <Autocomplete
                data-cy="variant-rule-set"
                multiple
                options={ruleSetItems}
                getOptionLabel={getRuleSetLabel}
                value={selectedRuleSets}
                onChange={handleRuleSetsChange}
                renderInput={renderRuleSetInput}
                renderTags={renderRuleSetTags}
                isOptionEqualToValue={isOptionEqual}
                fullWidth
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button onClick={handleCancel}>Cancel</Button>
                <Button data-cy="variant-submit" variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
                    {submitLabel()}
                </Button>
            </Stack>
        </Stack>
    )

    function dialogTitle() {
        return initialData ? "Edit Variant" : "Add Variant"
    }

    function submitLabel() {
        return initialData ? "Save" : "Add Variant"
    }

    function handleNameChange(e) {
        setName(e.target.value)
    }

    function handleRateCardChange(_, value) {
        setSelectedRateCard(value)
    }

    function handleRuleSetsChange(_, value) {
        setSelectedRuleSets(value)
    }

    function handleCancel() {
        cancel()
    }

    function handleSubmit() {
        ok({
            name: name.trim(),
            rateCardId: selectedRateCard.id,
            ruleSetIds: selectedRuleSets.map((rs) => rs.id),
        })
    }

    function getRateCardItems() {
        return rateCardResult?.items ?? []
    }

    function getOptionLabel(option) {
        return option?.name || ""
    }

    function getRuleSetLabel(option) {
        return option?.name || ""
    }

    function isOptionEqual(option, value) {
        return option?.id === value?.id
    }

    function renderRateCardInput(params) {
        return <TextField {...params} label="Rate Card" placeholder="Select rate card..." />
    }

    function renderRuleSetInput(params) {
        return <TextField {...params} label="Rule Sets (optional)" placeholder="No rules" />
    }

    function renderRuleSetTags(value, getTagProps) {
        return value.map((option, index) => (
            <Chip key={option.id} label={option.name} size="small" {...getTagProps({ index })} />
        ))
    }
}
